import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { getBoard, startPayment } from "@/lib/board.functions";
import { PACKAGES, formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { ScrollProgress } from "@/components/motion-kit";

export const Route = createFileRoute("/paket")({
  head: () => ({
    meta: [
      { title: "Paket Promosi — jasasebar.com" },
      {
        name: "description",
        content: "Sorotan, Pin Teratas, dan Boost 30 hari. Paket promosi berbayar untuk listing jasa Anda.",
      },
      { property: "og:title", content: "Paket Promosi — jasasebar.com" },
      { property: "og:description", content: "Sorotan, Pin Teratas, dan Boost 30 hari untuk listing jasa Anda." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/paket" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/paket" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Paket promosi jasasebar.com",
          itemListElement: Object.values(PACKAGES).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: p.name,
              description: p.desc,
              offers: { "@type": "Offer", price: p.price, priceCurrency: "IDR", availability: "https://schema.org/InStock" },
            },
          })),
        }),
      },
    ],
  }),
  component: PackagesPage,
});

const perks: Record<string, string[]> = {
  sorotan: ["Aksen biru pada kartu", "Label Sorotan", "Aktif 7 hari"],
  pin: ["Selalu di puncak papan", "Di atas semua peringkat bayar", "Aktif 24 jam"],
  boost: ["Sorotan 30 hari penuh", "Badge Boost", "Prioritas di kategori"],
};

function PackagesPage() {
  const boardFn = useServerFn(getBoard);
  const payFn = useServerFn(startPayment);
  const [slug, setSlug] = useState("");

  const { data } = useQuery({ queryKey: ["board"], queryFn: () => boardFn() });

  const pay = useMutation({
    mutationFn: (packageId: string) =>
      payFn({
        data: {
          slug,
          amount: PACKAGES[packageId]!.price,
          kind: "package" as const,
          packageId,
          origin: window.location.origin,
        },
      }),
    onSuccess: (res) => {
      window.location.href = res.paymentUrl;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />
      <main className="hero-canvas">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
          >
            <h1 className="font-display text-4xl font-semibold">Paket promosi</h1>
            <p className="mt-3 text-muted-foreground">
              Peringkat menentukan urutan; paket menentukan seberapa mencolok Anda terlihat. Keduanya bisa digabung.
            </p>
          </motion.div>

          <div className="surface-card mt-8 flex flex-wrap items-center gap-3 p-4">
            <label className="text-sm text-muted-foreground">Pilih listing:</label>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="h-11 min-w-64 rounded-xl border border-input bg-background px-3 text-sm"
            >
              <option value="">— pilih —</option>
              {(data?.listings ?? []).map((l) => (
                <option key={l.id} value={l.slug}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {Object.values(PACKAGES).map((pkg, i) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className={`surface-card flex flex-col p-6 ${pkg.id === "boost" ? "ring-1 ring-primary/40" : ""}`}
              >
                <h2 className="font-display text-xl font-semibold">{pkg.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{pkg.desc}</p>
                <div className="mt-4 font-display text-3xl font-semibold tabular-nums">{formatIDR(pkg.price)}</div>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-muted-foreground">
                  {perks[pkg.id]!.map((p) => (
                    <li key={p} className="flex items-center gap-2">
                      <Check className="size-4 text-success" /> {p}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => pay.mutate(pkg.id)}
                  disabled={!slug || pay.isPending}
                  className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {pay.isPending && <Loader2 className="size-4 animate-spin" />}
                  Ambil paket
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
